<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import type { Route, StopTimeData } from '@/@types/gtfs-ressource';
import RoundedButton from '@/components/ui/RoundedButton.vue';
import i18n from '@/i18n';
import { useAppStore } from '@/stores/app';
import { useGroupStore } from '@/stores/group';
import { useGtfsStore } from '@/stores/gtfs';
import { useCanonicalStopRedirect } from '@/use/canonical-stop-redirect';
import DayChips from './DayChips.vue';
import { timetableDays, weekdayLabel } from './days';
import RouteCard from './RouteCard.vue';
import type { TimetableDay, TimetableGroup } from './types';

const t = i18n.global.t;

const gtfsStore = useGtfsStore();
const groupStore = useGroupStore();
const appStore = useAppStore();

const router = useRouter();

const props = defineProps<{
  stopId: string;
}>();

useCanonicalStopRedirect(() => props.stopId);

const timerNow = ref<{ id: NodeJS.Timeout | null; ts: number }>({
  id: null,
  ts: Date.now() / 1000,
});

/**
 * Expanded directions, all collapsed on arrival. Kept as an explicit set rather
 * than derived from the groups, which recompute on every real-time poll and
 * would snap the cards shut. It survives a day change on purpose: a direction
 * missing from the new day simply has no card, so only the still-relevant ones
 * stay open.
 */
const openGroupKeys = ref<Set<string>>(new Set());

const email = computed<string | undefined>(() => groupStore.group?.contact.email);
const routes = computed<{ [routeId: string]: Route }>(() => gtfsStore.gtfsData.routes);
const stopName = computed<string>(() => (gtfsStore.gtfsData.stops[props.stopId] || {}).stop_name);
const stopIds = computed<Array<string>>(() => gtfsStore.stations[props.stopId] || [props.stopId]);

const days = computed<Array<TimetableDay>>(() =>
  timetableDays(new Date(timerNow.value.ts * 1000), groupStore.group?.tz || 'Europe/Paris', dayLabel),
);

const selectedDayKey = ref<string>(days.value[0].key);

const stopTimes = computed<Array<StopTimeData>>(() =>
  gtfsStore.getStopTimes(stopIds.value, selectedDayKey.value),
);

const groups = computed<Array<TimetableGroup>>(() => {
  const groups: { [groupKey: string]: TimetableGroup } = {};
  stopTimes.value.forEach(st => {
    const groupKey = JSON.stringify([st.route_id, st.trip_headsign]);
    if (!groups[groupKey]) {
      groups[groupKey] = {
        groupKey,
        routeId: st.route_id,
        tripHeadsign: st.trip_headsign,
        stopTimes: [],
      };
    }
    groups[groupKey].stopTimes.push(st);
  });

  Object.values(groups).forEach(group => {
    group.stopTimes.sort((a, b) => a.departure_ts - b.departure_ts);
  });

  return Object.keys(groups)
    .sort()
    .map(groupKey => groups[groupKey]);
});

// Past midnight the window slides, and a day selected before the rollover drops
// out of it: realign on the new today rather than show nothing. Realigning the
// key here rather than resolving it in a computed keeps `stopTimes` off the
// 30s ticker — `getStopTimes` rescans every calendar and every trip, so it must
// only run when the passenger actually picks another day.
watch(
  days,
  dayList => {
    if (!dayList.some(day => day.key === selectedDayKey.value)) {
      selectedDayKey.value = dayList[0].key;
    }
  },
  { immediate: true },
);

watch(
  () => stopIds.value,
  () => {
    appStore.setSelectedStopId(props.stopId);
  },
  { immediate: true },
);

onBeforeMount(() => {
  timerNow.value.id = setInterval(() => {
    timerNow.value.ts = Date.now() / 1000;
  }, 30000);
});

onBeforeUnmount(() => {
  if (timerNow.value.id) clearInterval(timerNow.value.id);
});

/** "Aujourd'hui", then the localized short weekday and day, e.g. "Mer. 5". */
function dayLabel(date: Date, offset: number): string {
  if (offset === 0) return t('timetable.today');

  return weekdayLabel(date, i18n.global.locale);
}

function isGroupOpen(group: TimetableGroup): boolean {
  return openGroupKeys.value.has(group.groupKey);
}

function toggleGroup(group: TimetableGroup) {
  const open = new Set(openGroupKeys.value);
  if (!open.delete(group.groupKey)) open.add(group.groupKey);
  openGroupKeys.value = open;
}
</script>

<template>
  <div class="page">
    <div class="content timetable-page">
      <header class="timetable-page__header">
        <RoundedButton
          :aria-label="$t('common.back')"
          icon="arrow-left"
          size="large"
          type="outline"
          @click="router.back()"
        />
        <h1 class="timetable-page__name">{{ stopName }}</h1>
      </header>

      <DayChips
        class="timetable-page__days"
        :days="days"
        :selected-key="selectedDayKey"
        @select="selectedDayKey = $event"
      />

      <div class="timetable-page__groups">
        <RouteCard
          v-for="group in groups"
          :key="group.groupKey"
          :group="group"
          :open="isGroupOpen(group)"
          :route="routes[group.routeId]"
          @toggle="toggleGroup(group)"
        />

        <!-- No departure at all on the selected day -->
        <p v-if="groups.length === 0" class="timetable-page__empty">
          <a v-if="email" :href="`mailto:${email}`" class="timetable-page__contact">
            {{ $t('timetable.groupContact') }}
          </a>
          <template v-else>{{ $t('timetable.groupContact') }}</template>
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.timetable-page {
  background-color: $color-white;
  color: $color-neutral-highest;

  &__header {
    align-items: center;
    display: flex;
    padding: $spacing-1 $spacing-4;
    position: relative;
  }

  &__name {
    font: $heading-h3;
    left: 50%;
    letter-spacing: $heading-h3-spacing;
    margin: 0;
    // Centered on the page, not on the space left over by the back button.
    max-width: 60%;
    overflow: hidden;
    position: absolute;
    text-overflow: ellipsis;
    transform: translateX(-50%);
    white-space: nowrap;
  }

  // The day stays reachable once the passenger has scrolled into the departures
  &__days {
    background-color: $color-white;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  &__groups {
    display: flex;
    flex-direction: column;
    gap: $spacing-4;
    padding: $spacing-3 $spacing-5;
  }

  &__empty {
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
    margin: 0;
    padding: $spacing-5 0;
    text-align: center;
  }

  &__contact {
    color: $color-neutral-highest;
    text-decoration: underline;
  }
}
</style>
